"use client";

import Link from "next/link";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import {
  Wallet,
  Package,
  BarChart3,
  Megaphone,
  Zap,
  Clock,
  MessageCircle,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";

// Ссылка на Telegram-бота
// const BOT_URL = "https://t.me/mpunit_diagnostics_bot";
const BOT_URL = "https://t.me/rnpsales_bot";

// 5 зон диагностики
const zones = [
  { icon: Wallet, name: "Финансовый учёт", color: "#a855f7", score: 4.2 },
  { icon: Package, name: "Управление запасами", color: "#3b82f6", score: 5.8 },
  { icon: BarChart3, name: "Аналитика и отчёты", color: "#22c55e", score: 3.1 },
  { icon: Megaphone, name: "Рекламная эффективность", color: "#f59e0b", score: 6.5 },
  { icon: Zap, name: "Операционные потери", color: "#ef4444", score: 4.9 },
];

// Примеры вопросов из реального бота
const sampleQuestions = [
  {
    question: "Как ты считаешь прибыль по каждому товару?",
    options: [
      "Точно по каждому артикулу с учётом всех расходов",
      "Примерно по категориям",
      "Только общую прибыль по магазину",
      "Смотрю только на выручку",
    ],
    highlight: 3,
  },
  {
    question: "Через сколько дней закончится каждый товар на складе?",
    options: [
      "Знаю точно по каждому размеру",
      "Примерно по основным позициям",
      "Узнаю когда уже заканчивается",
      "Замечаю только когда уже ноль",
    ],
    highlight: 2,
  },
  {
    question: "Назови 3 самых убыточных товара в своём магазине",
    options: [
      "Знаю и уже работаю над ними",
      "Подозреваю, но точно не считал",
      "Никогда об этом не задумывался",
    ],
    highlight: 1,
  },
];

// Типичные находки (вместо фейковых отзывов)
const findings = [
  {
    stat: "80%",
    text: "селлеров имеют убыточные товары среди самых ходовых позиций — и не знают об этом",
  },
  {
    stat: "38%",
    text: "селлеров работают в убыток, потому что видят выручку, а не прибыль после всех расходов",
  },
  {
    stat: "15-25%",
    text: "рекламного бюджета уходит на ключевики с показами, но нулевыми заказами",
  },
  {
    stat: "6.5%",
    text: "средняя чистая маржа в 2026 — ошибка в 2-3% переворачивает прибыльный товар в убыточный",
  },
];

// CTA кнопка
function BotCTA({ size = "default" }: { size?: "default" | "large" }) {
  return (
    <a
      href={BOT_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 bg-[#2AABEE] text-white font-semibold rounded-xl hover:bg-[#229ED9] transition-colors ${
        size === "large" ? "px-8 py-4 text-base" : "px-6 py-3 text-sm"
      }`}
    >
      <MessageCircle className="w-5 h-5" />
      Пройти диагностику в Telegram
    </a>
  );
}

// Мокап радар-диаграммы
function RadarMockup() {
  const centerX = 150;
  const centerY = 150;
  const radius = 110;
  const angles = zones.map((_, i) => (Math.PI * 2 * i) / zones.length - Math.PI / 2);

  // Точки для каждой зоны (score из 10 → пропорция радиуса)
  const points = zones.map((z, i) => {
    const r = (z.score / 10) * radius;
    return {
      x: centerX + r * Math.cos(angles[i]),
      y: centerY + r * Math.sin(angles[i]),
    };
  });

  const polygonPoints = points.map((p) => `${p.x},${p.y}`).join(" ");

  // Линии сетки (20%, 40%, 60%, 80%, 100%)
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 300 300" className="w-full max-w-[280px]">
        {/* Сетка */}
        {gridLevels.map((level) => (
          <polygon
            key={level}
            points={angles
              .map(
                (a) =>
                  `${centerX + radius * level * Math.cos(a)},${centerY + radius * level * Math.sin(a)}`
              )
              .join(" ")}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
        ))}

        {/* Оси */}
        {angles.map((a, i) => (
          <line
            key={i}
            x1={centerX}
            y1={centerY}
            x2={centerX + radius * Math.cos(a)}
            y2={centerY + radius * Math.sin(a)}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
        ))}

        {/* Заполненная область */}
        <polygon
          points={polygonPoints}
          fill="rgba(168,85,247,0.15)"
          stroke="#a855f7"
          strokeWidth="2"
        />

        {/* Точки */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="4"
            fill={zones[i].color}
          />
        ))}

        {/* Лейблы зон */}
        {zones.map((z, i) => {
          const labelR = radius + 28;
          const x = centerX + labelR * Math.cos(angles[i]);
          const y = centerY + labelR * Math.sin(angles[i]);
          return (
            <text
              key={z.name}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              className="text-[9px] fill-white/40"
            >
              {z.score.toFixed(1)}
            </text>
          );
        })}
      </svg>

      {/* Легенда */}
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-4">
        {zones.map((z) => (
          <div key={z.name} className="flex items-center gap-1.5">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: z.color }}
            />
            <span className="text-[11px] text-white/40">{z.name}</span>
            <span
              className="text-[11px] font-medium"
              style={{ color: z.score < 5 ? "#ef4444" : z.color }}
            >
              {z.score.toFixed(1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Мокап вопроса бота
function QuestionMockup({
  question,
  options,
  highlight,
}: {
  question: string;
  options: string[];
  highlight: number;
}) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-5">
      <p className="text-sm font-medium text-white mb-3">{question}</p>
      <div className="space-y-2">
        {options.map((opt, i) => (
          <div
            key={i}
            className={`text-xs px-3 py-2 rounded-lg border ${
              i === highlight
                ? "border-[#2AABEE]/30 bg-[#2AABEE]/10 text-[#2AABEE]"
                : "border-white/[0.06] text-white/40"
            }`}
          >
            {opt}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DiagnosticsLanding() {
  return (
    <div className="min-h-screen bg-[#050507] text-white">
      {/* Навигация — фиксированная */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050507]/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="flex items-center justify-between max-w-5xl mx-auto px-6 py-4">
        <Link href="/" className="flex items-center gap-2">
          <img src="/favicon.png" alt="MPUNIT" className="w-7 h-7" />
          <span className="text-white font-semibold">MPUNIT</span>
        </Link>
        <Link
          href="/"
          className="text-sm text-white/50 hover:text-white transition-colors"
        >
          ← На главную
        </Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="pt-28 pb-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <ScrollReveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#2AABEE]/30 bg-[#2AABEE]/10 px-4 py-2 mb-8">
              <MessageCircle className="w-4 h-4 text-[#2AABEE]" />
              <span className="text-sm text-[#2AABEE]">Telegram-бот</span>
              <span className="text-sm text-white/50">· 3 минуты · бесплатно</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
              <span className="text-white">Где ваш бизнес</span>
              <br />
              <span className="text-white/40">теряет деньги?</span>
            </h1>

            <p className="text-lg text-white/50 max-w-xl mx-auto mb-10 leading-relaxed">
              15 вопросов → персональный AI-отчёт с радар-диаграммой →
              конкретные рекомендации что исправить первым. В Telegram, без регистрации.
            </p>

            <BotCTA size="large" />
          </ScrollReveal>
        </div>
      </section>

      {/* МОКАП ОТЧЁТА + ПРИМЕРЫ ВОПРОСОВ — два столбца */}
      <section className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Вот что вы получите
            </h2>
            <p className="text-base text-white/50 text-center mb-16 max-w-xl mx-auto">
              Слева — примеры вопросов. Справа — ваш персональный отчёт.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Левая колонка: примеры вопросов */}
            <div>
              <ScrollReveal>
                <p className="text-xs text-white/30 uppercase tracking-wider mb-4">
                  Примеры вопросов из квиза
                </p>
              </ScrollReveal>
              <div className="space-y-4">
                {sampleQuestions.map((q, i) => (
                  <ScrollReveal key={i} delay={i * 0.1}>
                    <QuestionMockup
                      question={q.question}
                      options={q.options}
                      highlight={q.highlight}
                    />
                  </ScrollReveal>
                ))}
              </div>
              <ScrollReveal delay={0.3}>
                <p className="text-xs text-white/30 mt-4 text-center">
                  + ещё 12 вопросов с вариантами ответов
                </p>
              </ScrollReveal>
            </div>

            {/* Правая колонка: мокап отчёта */}
            <div>
              <ScrollReveal>
                <p className="text-xs text-white/30 uppercase tracking-wider mb-4">
                  Ваш персональный отчёт
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.15}>
                <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6">
                  {/* Радар-диаграмма */}
                  <RadarMockup />

                  {/* Общий балл */}
                  <div className="text-center mt-6 mb-6">
                    <div className="text-3xl font-bold text-white">4.9 <span className="text-base font-normal text-white/30">/ 10</span></div>
                    <p className="text-xs text-white/40 mt-1">Общая оценка здоровья бизнеса</p>
                  </div>

                  {/* Пример рекомендаций */}
                  <div className="border-t border-white/[0.06] pt-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-white">Аналитика: 3.1 из 10</p>
                        <p className="text-xs text-white/40">Вы тратите 4+ часа в неделю на ручной анализ, но не знаете свои убыточные товары</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-white">Финансы: 4.2 из 10</p>
                        <p className="text-xs text-white/40">Себестоимость не учитывается по партиям — прибыль считается неточно</p>
                      </div>
                    </div>
                  </div>

                  {/* Быстрая победа */}
                  <div className="mt-4 rounded-lg bg-emerald-500/[0.08] border border-emerald-500/20 p-3">
                    <p className="text-xs font-medium text-emerald-400">Быстрая победа за 7 дней:</p>
                    <p className="text-xs text-white/50 mt-1">
                      Найдите 3 самых убыточных артикула и поднимите на них цену или отключите — это сэкономит ₽15-40К/мес
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* ТИПИЧНЫЕ НАХОДКИ (вместо фейковых отзывов) */}
      <section className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Что находит диагностика
            </h2>
            <p className="text-base text-white/50 text-center mb-16">
              Типичные результаты селлеров, прошедших квиз
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {findings.map((f, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="flex items-start gap-4">
                  <div className="text-2xl font-bold text-purple-400 shrink-0 w-16 text-right">
                    {f.stat}
                  </div>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {f.text}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* КАК ЭТО РАБОТАЕТ */}
      <section className="py-24 px-6 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
              3 минуты — и всё понятно
            </h2>
          </ScrollReveal>

          <div className="space-y-8">
            {[
              {
                num: "01",
                title: "Откройте бота",
                desc: "Нажмите кнопку — откроется Telegram. Ни регистрации, ни карты.",
                time: "5 сек",
              },
              {
                num: "02",
                title: "Ответьте на 15 вопросов",
                desc: "Кнопки с вариантами — ничего печатать не нужно. Между вопросами — полезные инсайты с конкретными цифрами.",
                time: "3 мин",
              },
              {
                num: "03",
                title: "Получите AI-отчёт",
                desc: "Радар-диаграмма по 5 зонам, топ-3 узких места, быстрая победа за 7 дней и стратегическая рекомендация.",
                time: "мгновенно",
              },
            ].map((step, i) => (
              <ScrollReveal key={step.num} delay={i * 0.1}>
                <div className="flex items-start gap-6">
                  <div className="text-3xl font-bold text-white/[0.08] shrink-0">
                    {step.num}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-semibold text-white">
                        {step.title}
                      </h3>
                      <span className="text-xs text-white/30 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {step.time}
                      </span>
                    </div>
                    <p className="text-sm text-white/50">{step.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ФИНАЛЬНЫЙ CTA */}
      <section className="py-32 px-6 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              3 минуты в Telegram —
              <br />
              <span className="text-white/40">
                и вы знаете, где теряете деньги
              </span>
            </h2>
            <p className="text-lg text-white/50 mb-10 max-w-xl mx-auto">
              Персональный AI-отчёт. Радар-диаграмма по 5 зонам.
              Конкретный план что исправить первым. Бесплатно.
            </p>
            <BotCTA size="large" />
            <p className="text-xs text-white/30 mt-4">
              Без регистрации. Без карты. Просто откройте Telegram.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Мини-футер */}
      <footer className="py-8 px-6 border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link href="/" className="flex items-center gap-2">
            <img src="/favicon.png" alt="MPUNIT" className="w-5 h-5" />
            <span className="text-sm text-white/50">MPUNIT</span>
          </Link>
          <p className="text-xs text-white/30">&copy; 2026 — ООО «МП-ЮНИТ»</p>
        </div>
      </footer>
    </div>
  );
}
